import { useStore } from 'effector-react';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import { useCursos } from '../../hooks/cursos/cursos.hook';
import { useResponsividade } from '../../hooks/responsividade/responsividade.hook';
import { ConfigsManager } from '../../infra/configs/configs.manager';
import analyticsService from '../../services/analytics/analytics.service';
import { useCurso } from '../../stores/curso/curso.hook';
import { $listaCurso } from '../../stores/lista-curso/lista-curso.store';
import { atualizaLoadingCursos } from '../../stores/loading-curso/loading-cursos.store';
import { useUsuario } from '../../stores/usuario/usuario.hook';
import { $isLead, usuarioAtualizado, usuarioMigrado } from '../../stores/usuario/usuario.store';
import { getTipoCurso, transformarTipoCurso } from '../../utils/redirect-home/redirect-home.types';
import ScreenError from '../screen-error';
import Sidebar from '../sidebar/sidebar.component';
import S from './app-container.styles';
import { AppHeader } from './app-header/app-header.component';
import { useCourseSelection } from './hooks/use-course-selection/use-course-selection';
import { useUsuarioDatalayer } from './hooks/usuario-datalayer/usuario-datalayer';

const { brand } = ConfigsManager.getInstance();

export const AppContainer: React.FC = () => {
  const [renderConteudo, setRenderConteudo] = useState(true);
  const { pathname } = useLocation();
  const isDisciplinasHome = pathname === '/disciplinas';
  const { erro: erroUsuario } = useUsuario();
  const { curso } = useCurso();
  const { desktop, mobile } = useResponsividade();
  const isLead = !!useStore($isLead);
  const listaCurso = useStore($listaCurso);
  const { erro: erroCursos, pendente: cursosPendente } = useCursos();
  const [pathnameReal, setPathnameReal] = useState<string | null>(null);
  const tipoCursoRef = useRef<string | undefined>(undefined);
  const courseSelection = useCourseSelection(listaCurso, isLead);
  const cursoSelecionadoLocal = localStorage.getItem('cursoSelecionado');

  useUsuarioDatalayer();
  atualizaLoadingCursos(cursosPendente);

  const pushPageLoadedDatalayer = useCallback((pathname: string, tipoCursoRef: string | undefined) => {
    analyticsService.pageLoaded({ path: pathname, brand, type: tipoCursoRef });
  }, []);

  const atualizaTipoCursoCandidato = useCallback(
    (pathnameReal: string) => {
      if (tipoCursoRef.current) {
        return pushPageLoadedDatalayer(pathnameReal, tipoCursoRef.current);
      }
      return usuarioAtualizado.watch((usuario) => {
        const tipoCurso = usuario?.curso?.tipo ? transformarTipoCurso[usuario?.curso?.tipo] : undefined;
        tipoCursoRef.current = tipoCurso ? getTipoCurso[tipoCurso] : undefined;
        pushPageLoadedDatalayer(pathnameReal, tipoCursoRef.current);
      });
    },
    [pushPageLoadedDatalayer],
  );

  useEffect(() => {
    if (pathname !== '/') {
      setPathnameReal(pathname);
    }
  }, [pathname]);

  useEffect(() => {
    if (pathnameReal) {
      if (isLead) {
        tipoCursoRef.current = curso?.tipo ? getTipoCurso[curso.tipo] : undefined;
        return pushPageLoadedDatalayer(pathnameReal, tipoCursoRef.current);
      }
      atualizaTipoCursoCandidato(pathnameReal);
    }
  }, [atualizaTipoCursoCandidato, isLead, pathnameReal, pushPageLoadedDatalayer, curso?.tipo]);

  useEffect(() => {
    const subscription$ = usuarioMigrado.watch(() => setRenderConteudo(false));
    return () => subscription$.unsubscribe();
  }, []);

  if (erroUsuario && erroUsuario.name !== 'NoContentException') {
    return <ScreenError.Fatal erro="ERRO_CARREGAR_DADOS_USUARIO" />;
  }

  if (erroCursos) {
    return <ScreenError.Fatal erro="ERRO_CARREGAR_CURSOS" />;
  }

  if (renderConteudo) {
    return (
      <S.AppContainer>
        {desktop && (
          <S.SidebarContainer data-testid="sidebar">
            <Sidebar />
          </S.SidebarContainer>
        )}
        <S.MainContainer>
          <AppHeader
            isLead={isLead}
            isDisciplinasHome={isDisciplinasHome}
            curso={curso}
            listaCurso={listaCurso}
            desktop={desktop}
            mobile={mobile}
            cursoSelecionadoLocal={cursoSelecionadoLocal}
            {...courseSelection}
          />
          <Outlet />
        </S.MainContainer>
      </S.AppContainer>
    );
  }

  return <Navigate to="/disciplinas" />;
};
